/**
 * F-506-FE-01: RegisterPage
 *
 * Hospital-specific doctor registration at /register/:hospitalSlug.
 * - Fetches hospital name from GET /api/register/:hospitalSlug/info
 * - Invalid/inactive slug shows "Registration unavailable." with no details (prevent enumeration)
 * - Fields: name, email, password (strength: min 12 chars, upper+lower+numeral+special),
 *   specialization (active specializations), primary language (active platform languages)
 * - Submits to POST /api/register/:hospitalSlug
 *
 * Design: Clean, institutional, trust-building. Clinical palette throughout.
 * Mobile-first. Touch targets 44px minimum.
 * RTL support via CSS logical properties.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface HospitalInfo {
  name: string;
  isActive: boolean;
}

interface Specialization {
  id: string;
  name: string;
}

interface PlatformLanguage {
  code: string;
  name: string;
  isActive: boolean;
}

interface PasswordStrength {
  minLength: boolean;
  hasUpper: boolean;
  hasLower: boolean;
  hasNumeral: boolean;
  hasSpecial: boolean;
  isValid: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function checkPasswordStrength(password: string): PasswordStrength {
  const minLength = password.length >= 12;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumeral = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*()\-_=+[\]{}|;:,.<>?]/.test(password);
  return {
    minLength,
    hasUpper,
    hasLower,
    hasNumeral,
    hasSpecial,
    isValid: minLength && hasUpper && hasLower && hasNumeral && hasSpecial,
  };
}

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'include', ...init });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${res.status}`;
    const err = new Error(msg) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Password strength indicator
// ---------------------------------------------------------------------------

function StrengthIndicator({ strength, show }: { strength: PasswordStrength; show: boolean }) {
  if (!show) return null;

  const rules = [
    { label: 'At least 12 characters', met: strength.minLength },
    { label: 'Uppercase letter', met: strength.hasUpper },
    { label: 'Lowercase letter', met: strength.hasLower },
    { label: 'Number', met: strength.hasNumeral },
    { label: 'Special character (!@#$%^&* etc.)', met: strength.hasSpecial },
  ];

  return (
    <div className="mt-2 space-y-1" role="list" aria-label="Password requirements">
      {rules.map((rule) => (
        <div
          key={rule.label}
          role="listitem"
          className="flex items-center gap-2"
        >
          {rule.met ? (
            <svg
              className="w-4 h-4 flex-shrink-0 text-safety-green"
              viewBox="0 0 16 16"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"
              />
            </svg>
          ) : (
            <svg
              className="w-4 h-4 flex-shrink-0 text-clinical-text-muted"
              viewBox="0 0 16 16"
              fill="currentColor"
              aria-hidden="true"
            >
              <circle cx="8" cy="8" r="6" fillOpacity="0.15" />
              <circle cx="8" cy="8" r="4.5" fillOpacity="0" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          )}
          <span
            className={`text-xs font-body ${rule.met ? 'text-safety-green' : 'text-clinical-text-muted'}`}
          >
            {rule.label}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function RegisterPage() {
  const { hospitalSlug } = useParams<{ hospitalSlug: string }>();
  const navigate = useNavigate();

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPasswordStrength, setShowPasswordStrength] = useState(false);
  const [specializationId, setSpecializationId] = useState('');
  const [primaryLanguageCode, setPrimaryLanguageCode] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const passwordStrength = checkPasswordStrength(password);

  // ---------------------------------------------------------------------------
  // Fetch hospital info
  // ---------------------------------------------------------------------------
  const {
    data: hospitalInfo,
    isLoading: isLoadingHospital,
    isError: isHospitalError,
  } = useQuery<HospitalInfo>({
    queryKey: ['hospital-info', hospitalSlug],
    queryFn: () =>
      fetchJson<HospitalInfo>(`${BASE}/api/register/${hospitalSlug ?? ''}/info`),
    enabled: Boolean(hospitalSlug),
    retry: false,
  });

  // ---------------------------------------------------------------------------
  // Fetch specializations
  // ---------------------------------------------------------------------------
  const { data: specializationsData } = useQuery<{ specializations: Specialization[] }>({
    queryKey: ['specializations'],
    queryFn: () =>
      fetchJson<{ specializations: Specialization[] }>(`${BASE}/api/specializations`),
    enabled: Boolean(hospitalInfo?.isActive),
  });

  // ---------------------------------------------------------------------------
  // Fetch platform languages
  // ---------------------------------------------------------------------------
  const { data: languagesData } = useQuery<{ languages: PlatformLanguage[] }>({
    queryKey: ['languages'],
    queryFn: () =>
      fetchJson<{ languages: PlatformLanguage[] }>(`${BASE}/api/languages`),
    enabled: Boolean(hospitalInfo?.isActive),
  });

  const specializations = specializationsData?.specializations ?? [];
  const languages = (languagesData?.languages ?? []).filter((l) => l.isActive);

  // ---------------------------------------------------------------------------
  // Auto-select if single option
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (specializations.length === 1 && !specializationId) {
      setSpecializationId(specializations[0].id);
    }
  }, [specializations, specializationId]);

  useEffect(() => {
    if (languages.length === 1 && !primaryLanguageCode) {
      setPrimaryLanguageCode(languages[0].code);
    }
  }, [languages, primaryLanguageCode]);

  // ---------------------------------------------------------------------------
  // Submit mutation
  // ---------------------------------------------------------------------------
  const registerMutation = useMutation({
    mutationFn: async () => {
      return fetchJson<{ message: string }>(`${BASE}/api/register/${hospitalSlug ?? ''}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, specializationId, primaryLanguageCode }),
      });
    },
    onSuccess: () => {
      navigate(`/login/${hospitalSlug ?? ''}`, {
        state: { registrationSuccess: true },
      });
    },
    onError: (err: unknown) => {
      const errWithStatus = err as Error & { status?: number };
      setSubmitError(
        errWithStatus.message || 'Registration failed. Please try again.',
      );
    },
  });

  const isFormValid =
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    passwordStrength.isValid &&
    specializationId.length > 0 &&
    primaryLanguageCode.length > 0;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isFormValid || registerMutation.isPending) return;
    setSubmitError(null);
    registerMutation.mutate();
  }

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------
  if (isLoadingHospital) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-clinical-amber border-t-transparent rounded-full animate-spin" />
          <p className="text-sm font-body text-clinical-text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Invalid / inactive slug — no details (prevent enumeration)
  // ---------------------------------------------------------------------------
  if (isHospitalError || !hospitalInfo?.isActive) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] px-4">
        <div className="clinical-card p-8 text-center max-w-sm w-full">
          <div className="w-12 h-12 rounded-full bg-clinical-cream-dark flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-6 h-6 text-clinical-text-muted"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
              />
            </svg>
          </div>
          <p className="font-body text-clinical-charcoal font-medium">
            Registration unavailable.
          </p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Registration form
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[var(--color-bg)] flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        {/* Hospital header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-clinical-charcoal flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-7 h-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z"
              />
            </svg>
          </div>
          <h1 className="font-heading text-2xl font-semibold text-clinical-charcoal leading-tight">
            {hospitalInfo.name}
          </h1>
          <p className="text-sm font-body text-clinical-text-muted mt-1">
            Doctor registration
          </p>
        </div>

        {/* Form card */}
        <div className="clinical-card p-6">
          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {/* Full name */}
            <div>
              <label htmlFor="reg-name" className="form-label">
                Full name
              </label>
              <input
                id="reg-name"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="form-input"
                placeholder="Dr. Jane Smith"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="reg-email" className="form-label">
                Email address
              </label>
              <input
                id="reg-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input"
                placeholder="you@hospital.com"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="reg-password" className="form-label">
                Password
              </label>
              <input
                id="reg-password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setShowPasswordStrength(true);
                }}
                onFocus={() => setShowPasswordStrength(true)}
                className="form-input"
                placeholder="At least 12 characters"
                aria-describedby="password-requirements"
              />
              <div id="password-requirements">
                <StrengthIndicator strength={passwordStrength} show={showPasswordStrength} />
              </div>
            </div>

            {/* Specialization */}
            <div>
              <label htmlFor="reg-specialization" className="form-label">
                Specialization
              </label>
              <select
                id="reg-specialization"
                required
                value={specializationId}
                onChange={(e) => setSpecializationId(e.target.value)}
                className="form-input"
              >
                <option value="" disabled>
                  {specializations.length === 0
                    ? 'Loading specializations...'
                    : 'Select specialization'}
                </option>
                {specializations.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Primary language */}
            <div>
              <label htmlFor="reg-language" className="form-label">
                Primary language
              </label>
              <select
                id="reg-language"
                required
                value={primaryLanguageCode}
                onChange={(e) => setPrimaryLanguageCode(e.target.value)}
                className="form-input"
              >
                <option value="" disabled>
                  {languages.length === 0 ? 'Loading languages...' : 'Select language'}
                </option>
                {languages.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Submit error */}
            {submitError && (
              <div
                role="alert"
                className="p-3 rounded-clinical bg-red-50 border border-red-200 text-sm font-body text-red-700"
              >
                {submitError}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={!isFormValid || registerMutation.isPending}
              className="btn-primary w-full"
            >
              {registerMutation.isPending ? (
                <>
                  <span
                    className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                    aria-hidden="true"
                  />
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </button>
          </form>
        </div>

        {/* Login link */}
        <p className="text-center text-sm font-body text-clinical-text-muted mt-6">
          Already registered?{' '}
          <Link
            to={`/login/${hospitalSlug ?? ''}`}
            className="text-clinical-teal hover:text-clinical-teal-dark underline-offset-2 hover:underline transition-colors"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
